import { type FormEvent, useEffect, useId, useState } from "react";
import { api, type Me, type TripCard } from "./api.ts";
import { tripDates } from "./format.ts";
import { useLoad } from "./hooks.ts";
import { linkIn } from "./linkIn.ts";
import { useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { openedOrder } from "./reopen.ts";
import { navigate, tripPath } from "./router.ts";
import { Button, Icon } from "./souvenir.ts";
import { Wordmark } from "./ui.tsx";

// A link shared to Tripmo from a phone's share sheet (features/links.md, "Save a link on the web"): the web app
// manifest's share_target sends what was shared here (/share?url=…&text=…&title=…; Android's Chrome, once Tripmo
// is installed; iPhones don't let web apps receive shares). It asks which trip, the last opened on this device
// first, takes a note (what was shared with the link, to change), and saves the link to that trip's inbox, as an AI
// saves one, for the AI to bring in when asked; then Undo, or on to the trip.

const MAX_NOTE = 1000;

// What was shared besides the link, as a note: its title, or its words without the link.
function noteOf(url: string, text: string | null, title: string | null): string {
  const words = (text ?? "")
    .replace(url, "")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return (title?.trim() || words).slice(0, MAX_NOTE);
}

export function ShareLink({
  me,
  url,
  text,
  title,
}: {
  me: Me;
  url: string | null;
  text: string | null;
  title: string | null;
}) {
  const id = useId();
  const link = linkIn(url) ?? linkIn(text);
  const trips = useLoad(api.trips, []);
  const online = useOnline();
  // The trips a link can go to (I'm in them, they can change), the last opened here first.
  const order = openedOrder(me.user.id);
  const choices: TripCard[] =
    trips.state === "ready"
      ? trips.data.trips
          .filter((t) => t.status === "member" && !t.locked)
          .sort((a, b) => rank(order, a.id) - rank(order, b.id))
      : [];
  const [trip, setTrip] = useState<string | null>(null);
  useEffect(() => {
    if (trip === null && choices[0]) setTrip(choices[0].id);
  }, [trip, choices]);
  const [note, setNote] = useState(() => (link ? noteOf(link, text, title) : ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ trip: TripCard; change: string } | null>(null);
  const [undone, setUndone] = useState(false);
  useEffect(() => {
    document.title = "Save a link · Tripmo";
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const to = choices.find((t) => t.id === trip);
    if (!link || !to || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { change } = await api.saveLink(to.id, { url: link, ...(note.trim() ? { note: note.trim() } : {}) });
      setSaved({ trip: to, change });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save the link. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    if (!saved) return;
    setBusy(true);
    try {
      await api.undo(saved.trip.id, saved.change);
      setUndone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't undo. Try again from the trip's Updates.");
    } finally {
      setBusy(false);
    }
  };

  const shown = link ? new URL(link) : null;
  return (
    <div className="tm-page">
      <header className="tm-bar">
        <Wordmark />
        <span className="tm-grow" />
        <Button variant="quiet" size="sm" onClick={() => navigate("/")}>
          Your trips
        </Button>
      </header>
      <main className="tm-shared">
        <h1 className="display-l tm-shared-title">Save a link</h1>
        {!link ? (
          <p className="body tm-muted tm-shared-words">
            Nothing to save: what was shared has no link in it. Share the page or the post itself to Tripmo.
          </p>
        ) : (
          <>
            <p className="tm-shared-link callout" title={link}>
              <Icon name="share" size={18} className="tm-shared-link-icon" />
              <span className="tm-shared-host">{shown?.hostname.replace(/^www\./, "")}</span>
              <span className="tm-shared-path tm-muted">{`${shown?.pathname ?? ""}${shown?.search ?? ""}`}</span>
            </p>
            {saved ? (
              <div className="tm-shared-saved" role="status">
                <p className="body tm-shared-words">
                  {undone ? (
                    <>Not saved: it's out of the links inbox of {saved.trip.title} again.</>
                  ) : (
                    <>
                      Saved to the links inbox of <strong>{saved.trip.title}</strong>. Say what it's about there with
                      About, or ask your AI to bring in what's in your links inbox.
                    </>
                  )}
                </p>
                <span className="tm-shared-actions">
                  <Button variant="primary" size="sm" onClick={() => navigate(tripPath(saved.trip.id, null, "ideas"))}>
                    Open {saved.trip.title}
                  </Button>
                  {!undone && (
                    <Button variant="quiet" size="sm" icon="replay" disabled={busy} onClick={undo}>
                      Undo
                    </Button>
                  )}
                </span>
              </div>
            ) : (
              <form className="tm-edit-form tm-shared-form" aria-label="Save a link" noValidate onSubmit={save}>
                <fieldset className="tm-shared-trips">
                  <legend className="sv-field-label">Which trip?</legend>
                  {trips.state === "loading" && <p className="caption tm-muted tm-shared-words">Your trips…</p>}
                  {trips.state === "ready" && choices.length === 0 && (
                    <p className="callout tm-shared-words">You're in no trip that can take a link yet.</p>
                  )}
                  {choices.map((t) => (
                    <label key={t.id} className={`tm-shared-trip${trip === t.id ? " is-chosen" : ""}`}>
                      <input
                        type="radio"
                        name={`${id}-trip`}
                        value={t.id}
                        checked={trip === t.id}
                        onChange={() => setTrip(t.id)}
                      />
                      <span className="tm-shared-trip-words">
                        <span className="callout">{t.title}</span>
                        {tripDates(t.dates) && <span className="caption tm-muted">{tripDates(t.dates)}</span>}
                      </span>
                    </label>
                  ))}
                </fieldset>
                <div className="sv-field tm-booking-field">
                  <label className="sv-field-label" htmlFor={`${id}-note`}>
                    A note, if you like
                  </label>
                  <input
                    id={`${id}-note`}
                    className="sv-field-input"
                    autoComplete="off"
                    maxLength={MAX_NOTE}
                    placeholder="Mei's ramen crawl video"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                {!online && <p className="caption tm-muted">No signal: saving needs signal.</p>}
                {error && (
                  <p className="caption tm-error" role="alert">
                    {error}
                    <ReportThis />
                  </p>
                )}
                <span className="tm-note-actions">
                  <Button variant="primary" size="sm" type="submit" disabled={busy || !trip || !online}>
                    {busy ? "Saving…" : "Save the link"}
                  </Button>
                </span>
              </form>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const rank = (order: string[], trip: string) => {
  const at = order.indexOf(trip);
  return at < 0 ? order.length : at;
};
